import React, { useEffect, useMemo, useState } from "react";
import { User } from "../types/user.types";
import UserDetails from "./UserDetails";
import useAllUsers from "../hooks/useAllUsers";
import { getAllCustomers } from "../../../services/customerService";
import { CognitoAuthService } from "../../../services/cognitoService";
import InviteUserSection from "./InviteUserSection";
import CreateBrandForCustomerSection from "./CreateBrandForCustomerSection";
import { useAuth } from "../../../context/AuthContext";
import { deleteUser } from "../services/userService";
import { Customer } from "../../../types/customer";
import useUserAssignment from "../hooks/useUserAssignment";
import { debug } from "../../../utils/debugLogger";
import ConfirmDialog from "../../../components/Shared/ConfirmDialog";

const PROFILE_LABEL =
  "mb-1.5 block text-xs font-medium uppercase tracking-wide text-theme-fg-muted";

const PROFILE_CONTROL_CLASS =
  "h-10 w-full rounded-md border border-theme-border bg-theme-background px-3 text-sm text-theme-fg shadow-sm focus:outline-none focus-visible:outline-none focus:border-theme-secondary focus:ring-2 focus:ring-theme-secondary/40 caret-theme-fg";

function userMatchesSearch(user: User, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const name = (user.display_name || "").toLowerCase();
  const email = (user.email || "").toLowerCase();
  return name.includes(q) || email.includes(q);
}

/** True when this row is the signed-in user (DELETE returns 400 CannotDeleteSelf). */
function isCurrentUserRow(user: User, idToken: string | null): boolean {
  if (!idToken) return false;
  try {
    const payload = CognitoAuthService.parseTokenPayload(idToken);
    const sub = typeof payload.sub === "string" ? payload.sub : "";
    const tokenEmail =
      typeof payload.email === "string" ? payload.email.toLowerCase() : "";
    if (tokenEmail && user.email?.toLowerCase() === tokenEmail) {
      return true;
    }
    if (sub) {
      if (user.user_id === sub || user.user_id === `user_id_${sub}`) {
        return true;
      }
      if (user.user_id.endsWith(sub)) {
        return true;
      }
    }
  } catch {
    /* ignore parse errors */
  }
  return false;
}

const AllUsers: React.FC = () => {
  const { idToken } = useAuth();
  const { users, loading, error, fetchUsers, canManageUsers } = useAllUsers();
  const {
    assignUser,
    loading: assignmentLoading,
    error: assignmentError,
  } = useUserAssignment();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null
  );
  const [assignmentMessage, setAssignmentMessage] = useState<string | null>(
    null
  );
  const [userListMessage, setUserListMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [pendingDeleteUser, setPendingDeleteUser] = useState<User | null>(null);
  const [userSearchQuery, setUserSearchQuery] = useState("");

  const filteredUsers = useMemo(
    () => users.filter((u) => userMatchesSearch(u, userSearchQuery)),
    [users, userSearchQuery]
  );

  const handleUserSelect = (user: User) => {
    setSelectedUser(user);
    setShowUserDetails(true);
  };

  const handleUserSave = async (userData: Partial<User>) => {
    if (!selectedUser) return;

    try {
      // Update user logic will be handled in UserDetails component
      // Just refresh the list and close the details view
      await fetchUsers();
      setShowUserDetails(false);
      setSelectedUser(null);
    } catch (err: any) {
      debug.error("Error updating user:", err);
      throw err; // Let the UserDetails component handle the error
    }
  };

  // SGL-555: list the Brand just created for a customer and select it, so the
  // admin can check its Owner or invite more people straight away. The user
  // list is not refetched here: fetchUsers swaps the whole page for a spinner,
  // which would unmount the card and lose its result message.
  const handleBrandCreated = async (customerId: string) => {
    try {
      const customerList = await getAllCustomers(idToken);
      setCustomers(customerList);
      setSelectedCustomer(customerList.find((c: Customer) => c.customer_id === customerId) ?? null);
    } catch (err) {
      debug.error("Error reloading customers:", err);
    }
  };

  const handleBackToList = () => {
    setShowUserDetails(false);
    setSelectedUser(null);
  };

  const loadCustomers = async () => {
    if (loadingCustomers || customers.length > 0) return;

    try {
      setLoadingCustomers(true);
      const customerList = await getAllCustomers(idToken);
      setCustomers(customerList);
    } catch (err) {
      debug.error("Error loading customers:", err);
    } finally {
      setLoadingCustomers(false);
    }
  };

  useEffect(() => {
    if (!idToken) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingCustomers(true);
        const customerList = await getAllCustomers(idToken);
        if (!cancelled) {
          setCustomers(customerList);
        }
      } catch (err) {
        debug.error("Error loading customers:", err);
      } finally {
        if (!cancelled) {
          setLoadingCustomers(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [idToken]);

  const handleCustomerSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const customerId = e.target.value;
    if (!customerId) {
      setSelectedCustomer(null);
      return;
    }

    const customer = customers.find((c) => c.customer_id === customerId);
    setSelectedCustomer(customer || null);
  };

  const handleDeleteUser = async (user: User) => {
    if (!idToken) {
      setUserListMessage({ type: "error", text: "You are not signed in." });
      return;
    }
    if (isCurrentUserRow(user, idToken)) {
      setUserListMessage({
        type: "error",
        text: "You cannot remove your own account from here.",
      });
      return;
    }
    setPendingDeleteUser(user);
  };

  const confirmDeleteUser = async (user: User) => {
    if (!idToken) return;
    setUserListMessage(null);
    try {
      setDeletingUserId(user.user_id);
      await deleteUser(user.user_id, idToken);
      await fetchUsers();
      setUserListMessage({
        type: "success",
        text: `Removed ${user.display_name} from the directory.`,
      });
    } catch (err) {
      const text =
        err instanceof Error ? err.message : "Failed to remove user.";
      setUserListMessage({ type: "error", text });
    } finally {
      setDeletingUserId(null);
      setPendingDeleteUser(null);
    }
  };

  const handleAssignUser = async (user: User) => {
    if (!selectedCustomer) {
      setAssignmentMessage("Select a business profile first.");
      return;
    }

    try {
      // More detailed logging of the user object and groups
      debug.log("User object for assignment:", user);
      debug.log("User groups type:", typeof user.groups);
      debug.log("User groups value:", user.groups);
      
      await assignUser(user.user_id, selectedCustomer.customer_id, user.groups);
      setAssignmentMessage(
        `Successfully assigned ${user.display_name} to ${selectedCustomer.business_name}`
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to assign user.";
      setAssignmentMessage(msg);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#2f4941]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-600">
          {error}
        </div>
      </div>
    );
  }

  if (!canManageUsers) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-600">
          You don't have permission to access this page
        </div>
      </div>
    );
  }

  if (showUserDetails && selectedUser) {
    return (
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">User Details</h2>
          <button
            onClick={handleBackToList}
            className="px-4 py-2 border border-[#2f4941] text-[#2f4941] rounded-md hover:bg-[#eaf3ef] transition-colors"
          >
            Back to User List
          </button>
        </div>

        <UserDetails
          user={selectedUser}
          canEditGroups={true}
          onSave={handleUserSave}
        />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-theme-fg">User Management</h2>
        <span className="rounded-full border border-theme-border bg-theme-surface px-3 py-1 text-xs font-medium text-theme-fg-muted">
          Super Admin
        </span>
      </div>

      {/* Business profile + Invite (two-column cards) */}
      <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-stretch">
        <div className="flex h-full min-h-0 flex-col rounded-lg border border-theme-border bg-theme-surface p-6 shadow-sm">
          <div className="mb-4 flex items-center gap-2">
            <span
              className="material-icons text-xl text-theme-fg-muted"
              aria-hidden
            >
              business
            </span>
            <h3 className="text-lg font-semibold text-theme-fg">
              Business profile &amp; assignment
            </h3>
          </div>
          <p className="mb-5 text-sm leading-relaxed text-theme-fg-muted">
            Choose a business profile for invitations and for assigning existing
            users from the list below.
          </p>

          <div className="space-y-4">
            <div>
              <label htmlFor="admin-business-profile" className={PROFILE_LABEL}>
                Business profile
              </label>
              <select
                id="admin-business-profile"
                className={PROFILE_CONTROL_CLASS}
                onChange={handleCustomerSelect}
                onClick={loadCustomers}
                value={selectedCustomer?.customer_id || ""}
              >
                <option value="">-- Select a business profile --</option>
                {customers.map((customer) => (
                  <option
                    key={customer.customer_id}
                    value={customer.customer_id}
                  >
                    {customer.business_name || customer.customer_id}
                  </option>
                ))}
              </select>
              {loadingCustomers && (
                <p className="mt-1.5 text-sm text-theme-fg-muted">
                  Loading business profiles…
                </p>
              )}
            </div>

            <div>
              <label htmlFor="admin-viewer-role" className={PROFILE_LABEL}>
                Assigned role
              </label>
              <select
                id="admin-viewer-role"
                className={`${PROFILE_CONTROL_CLASS} text-theme-fg-muted`}
                disabled
                value="SuperAdmin"
              >
                <option value="SuperAdmin">SuperAdmin</option>
              </select>
            </div>
          </div>

          {selectedCustomer && (
            <div className="mt-5 border-t border-theme-border pt-4">
              <p className="text-sm font-medium text-theme-fg">
                {selectedCustomer.business_name}
              </p>
              <div className="mt-2 space-y-1 text-sm text-theme-fg-muted">
                <p>
                  Location: {selectedCustomer.city}, {selectedCustomer.state}
                </p>
                <p>Contact: {selectedCustomer.contact_email}</p>
              </div>
            </div>
          )}
        </div>

        <div className="flex h-full min-h-0 flex-col rounded-lg border border-theme-border bg-theme-surface p-6 shadow-sm">
          <InviteUserSection
            idToken={idToken}
            selectedCustomer={selectedCustomer}
            onSuccess={fetchUsers}
            embedded
          />
        </div>
      </div>

      <div className="mb-6">
        <CreateBrandForCustomerSection idToken={idToken} customers={customers} onCreated={handleBrandCreated} />
      </div>

      {assignmentMessage && (
        <div
          className={`mb-6 rounded-md border p-3 text-sm ${
            assignmentMessage.includes("Successfully")
              ? "border-emerald-200 bg-emerald-50 text-emerald-900"
              : "border-red-200 bg-red-50 text-red-800"
          }`}
        >
          {assignmentMessage}
        </div>
      )}

      {userListMessage && (
        <div
          role="status"
          className={`mb-6 rounded-md border p-3 text-sm ${
            userListMessage.type === "success"
              ? "border-emerald-200 bg-emerald-50 text-emerald-900"
              : "border-red-200 bg-red-50 text-red-800"
          }`}
        >
          {userListMessage.text}
        </div>
      )}

      {/* Users Table */}
      <div className="overflow-hidden rounded-lg border border-theme-border bg-theme-surface shadow-sm">
        <div className="border-b border-theme-border px-4 py-3 sm:px-6">
          <label htmlFor="user-list-search" className={PROFILE_LABEL}>
            Search users
          </label>
          <input
            id="user-list-search"
            type="search"
            value={userSearchQuery}
            onChange={(e) => setUserSearchQuery(e.target.value)}
            placeholder="Search by name or email"
            autoComplete="off"
            className={`max-w-md ${PROFILE_CONTROL_CLASS} placeholder:text-slate-400`}
          />
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Name
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Email
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Role
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Status
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {users.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                  No users found
                </td>
              </tr>
            ) : filteredUsers.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-6 py-4 text-center text-theme-fg-muted"
                >
                  No users match your search.
                </td>
              </tr>
            ) : (
              filteredUsers.map((user) => (
                <tr key={user.user_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {user.display_name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{user.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                      ${
                        user.groups === "SuperAdmin"
                          ? "bg-purple-100 text-purple-800"
                          : user.groups === "Internal"
                          ? "bg-green-100 text-green-800"
                          : "bg-blue-100 text-blue-800"
                      }`}
                    >
                      {user.groups}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                      ${
                        user.status === "ACTIVE"
                          ? "bg-green-100 text-green-800"
                          : user.status === "INACTIVE" || user.status === "NOT_ACTIVE"
                          ? "bg-red-100 text-red-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {user.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                      <button
                        type="button"
                        onClick={() => handleUserSelect(user)}
                        className="text-theme-primary hover:opacity-90"
                      >
                        View/Edit
                      </button>
                      {user.groups !== "SuperAdmin" && selectedCustomer && (
                        <button
                          type="button"
                          onClick={() => handleAssignUser(user)}
                          disabled={assignmentLoading}
                          className={
                            !assignmentLoading
                              ? "text-theme-primary hover:opacity-90"
                              : "cursor-not-allowed text-theme-fg-muted"
                          }
                        >
                          {assignmentLoading
                            ? "Assigning..."
                            : `Assign to ${
                                selectedCustomer.business_name || "Customer"
                              }`}
                        </button>
                      )}
                      {!isCurrentUserRow(user, idToken) && (
                        <button
                          type="button"
                          onClick={() => handleDeleteUser(user)}
                          disabled={deletingUserId === user.user_id}
                          className="text-theme-danger hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {deletingUserId === user.user_id
                            ? "Removing…"
                            : "Remove user"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <ConfirmDialog
        isOpen={pendingDeleteUser !== null}
        title={
          pendingDeleteUser
            ? `Remove ${pendingDeleteUser.display_name}?`
            : "Remove user?"
        }
        message={
          pendingDeleteUser
            ? `Remove ${pendingDeleteUser.display_name} (${pendingDeleteUser.email})? This cannot be undone.`
            : ""
        }
        confirmLabel="Remove user"
        destructive
        isConfirming={deletingUserId !== null}
        onConfirm={() => {
          if (pendingDeleteUser) void confirmDeleteUser(pendingDeleteUser);
        }}
        onCancel={() => setPendingDeleteUser(null)}
      />
    </div>
  );
};

export default AllUsers;
